import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { getMeetingAnalyticsAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { companyUrl } from '../../../utils/mhNav';
import styles from './MeetingAnalytics.module.scss';

export default function MeetingAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [compare, setCompare] = useState(false);
  const [drill, setDrill] = useState(null);
  const [filters, setFilters] = useState({ department: 'all', channel: 'all' });
  const [range, setRange] = useState({ from: '', to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMeetingAnalyticsAPI(range, { ...filters, statusDrill: drill || undefined });
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [range, filters, drill]);

  useEffect(() => {
    load();
  }, [load]);

  const chartOptions = useMemo(() => {
    if (!data) return {};
    return {
      chart: { toolbar: { show: true } },
      stroke: { width: [2, 2, 2, ...(compare ? [2, 2, 2] : [])], dashArray: [...(compare ? [0, 0, 0, 5, 5, 5] : [0, 0, 0])] },
      xaxis: { categories: data.trendWeeks },
      legend: { show: true },
    };
  }, [data, compare]);

  const chartSeries = useMemo(() => {
    if (!data) return [];
    const base = [
      { name: 'Created', data: data.trendCreated },
      { name: 'Cancelled', data: data.trendCancelled },
      { name: 'Conflicts', data: data.trendConflicts },
    ];
    if (!compare) return base;
    return [
      ...base,
      { name: 'Created (prev)', data: data.trendCreated.map((v) => Math.max(0, v - 10)) },
      { name: 'Cancelled (prev)', data: data.trendCancelled.map((v) => Math.max(0, v - 2)) },
      { name: 'Conflicts (prev)', data: data.trendConflicts.map((v) => Math.max(0, v - 1)) },
    ];
  }, [data, compare]);

  const columns = useMemo(
    () => [
      { name: 'Meeting', selector: (r) => r.meetingTitle },
      { name: 'Date', selector: (r) => new Date(r.date).toLocaleString() },
      { name: 'Organizer', selector: (r) => r.organizer },
      { name: 'Status', selector: (r) => r.status },
      { name: 'Channel', selector: (r) => r.channel },
      { name: 'Department', selector: (r) => r.department },
    ],
    []
  );

  const events = useMemo(() => {
    let e = data?.events || [];
    if (drill) e = e.filter((x) => x.status === drill);
    return e;
  }, [data, drill]);

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Meeting analytics" breadcrumbs={[{ label: 'Company', path: companyUrl('reports') }]} />
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  const m = data?.metrics;

  return (
    <div>
      <PageHeader
        title="Meeting analytics"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Reports', path: companyUrl('reports') },
          { label: 'Analytics' },
        ]}
      />
      {loading && !data ? (
        <LoadingSkeleton rows={5} cols={4} />
      ) : (
        <>
          <Row className="g-2 mb-3">
            <Col md={2}>
              <Form.Control type="date" size="sm" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            </Col>
            <Col md={2}>
              <Form.Control type="date" size="sm" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}>
                <option value="all">All departments</option>
                <option value="Engineering">Engineering</option>
                <option value="Sales">Sales</option>
                <option value="Ops">Ops</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={filters.channel} onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))}>
                <option value="all">All channels</option>
                <option value="Teams">Teams</option>
                <option value="WhatsApp">WhatsApp</option>
                <option value="Web">Web</option>
              </Form.Select>
            </Col>
          </Row>

          <Row className="g-2 mb-3">
            {[
              { k: 'created', label: 'Created', v: m?.created },
              { k: 'rescheduled', label: 'Rescheduled', v: m?.rescheduled },
              { k: 'cancelled', label: 'Cancelled', v: m?.cancelled },
              { k: 'conflicts', label: 'Conflicts', v: m?.conflicts },
              { k: 'transcript', label: 'Transcript success %', v: m?.transcriptSuccessPct },
            ].map((x) => (
              <Col key={x.k} md={6} lg={4} xl={2}>
                <Card
                  className={`${styles.metric} h-100`}
                  role={x.k === 'cancelled' ? 'button' : undefined}
                  onClick={() => (x.k === 'cancelled' ? setDrill((d) => (d === 'cancelled' ? null : 'cancelled')) : null)}
                >
                  <Card.Body>
                    <div className="small text-muted">{x.label}</div>
                    <div className="h4 mb-0">{x.v}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="d-flex align-items-center gap-2 mb-2">
            <Form.Check type="switch" label="Compare previous period" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
            {drill && (
              <Button size="sm" variant="light" onClick={() => setDrill(null)}>
                Clear table filter
              </Button>
            )}
          </div>
          <div className="border rounded p-2 mb-3 bg-white">
            <Chart options={chartOptions} series={chartSeries} type="line" height={320} />
          </div>

          <h6 className="mb-2">Raw events</h6>
          <DataTableWrapper columns={columns} data={events} loading={false} showSearch />
        </>
      )}
    </div>
  );
}
